import type { EarningsRow } from '../../../types/tickerDetail';

/** One reporting quarter, normalised for the charts and the table. */
export interface EarningsPoint {
  /** Report date, YYYY-MM-DD. */
  date: string;
  /** Short axis label, e.g. "Q3 '25". */
  label: string;
  /** BMO/AMC string straight off the feed, for the report-window icon. */
  time: string;
  epsActual: number | null;
  epsEstimate: number | null;
  revenueActual: number | null;
  revenueEstimate: number | null;
  /** Fractional EPS surprise, e.g. 0.064 for +6.4%. Null when unmeasurable. */
  epsSurprise: number | null;
  /** Fractional revenue surprise. Null when unmeasurable. */
  revenueSurprise: number | null;
  /** True once the company has actually reported — drives the ghost styling. */
  reported: boolean;
}

/**
 * Fractional surprise of an actual against its estimate.
 *
 * The denominator is the ABSOLUTE estimate. Loss-making names carry negative
 * consensus, and a company that loses less than feared (-0.50 actual against a
 * -1.00 estimate) is a beat; a signed denominator reports that as -50%, which
 * then colours the bar red and drags the average the wrong way.
 *
 * A zero estimate has no meaningful percentage, so it yields null rather than
 * Infinity — those quarters drop out of the averages instead of poisoning them.
 */
export function surprisePct(
  actual: number | null | undefined,
  estimate: number | null | undefined
): number | null {
  if (actual == null || estimate == null) return null;
  if (estimate === 0) return null;
  return (actual - estimate) / Math.abs(estimate);
}

/** Calendar quarter label for a report date, e.g. "Q1 '26". */
export function quarterLabel(date: string): string {
  const [y, m] = date.split('-');
  const q = Math.floor((Number(m) - 1) / 3) + 1;
  return `Q${q} '${y.slice(2)}`;
}

/**
 * Sorts the feed oldest-first and derives the surprise figures.
 *
 * FMP returns newest-first and mixes scheduled future dates in with settled
 * history, so both have to be handled here rather than at the call site.
 */
export function prepareEarnings(rows: EarningsRow[], today: string): EarningsPoint[] {
  return [...rows]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((r) => ({
      date: r.date,
      label: quarterLabel(r.date),
      time: r.time ?? '',
      epsActual: r.eps ?? null,
      epsEstimate: r.epsEstimated ?? null,
      revenueActual: r.revenue ?? null,
      revenueEstimate: r.revenueEstimated ?? null,
      epsSurprise: surprisePct(r.eps, r.epsEstimated),
      revenueSurprise: surprisePct(r.revenue, r.revenueEstimated),
      // A row is history only once it has a printed EPS. Date alone is not
      // enough: a company that reported this morning still has date === today.
      reported: r.eps != null && r.date <= today,
    }));
}

/** Aggregate read on a run of quarters, for the tile row. */
export interface EarningsStatsSummary {
  /** Quarters where the actual met or cleared consensus. */
  beats: number;
  /** Quarters with both an actual and an estimate to compare. */
  measured: number;
  /** Mean fractional EPS surprise across measurable quarters. */
  avgEpsSurprise: number | null;
  /** Mean fractional revenue surprise across measurable quarters. */
  avgRevenueSurprise: number | null;
  /** Soonest scheduled report that has not printed yet. */
  next: EarningsPoint | null;
}

function mean(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Beat rate and average surprises over the reported quarters supplied.
 *
 * Only quarters carrying both an actual and an estimate count towards the
 * rate — a missing estimate is unknown, not a miss.
 */
export function summarise(
  points: EarningsPoint[],
  next: EarningsPoint | null
): EarningsStatsSummary {
  const measured = points.filter(
    (p) => p.reported && p.epsActual != null && p.epsEstimate != null
  );

  return {
    beats: measured.filter((p) => (p.epsActual as number) >= (p.epsEstimate as number)).length,
    measured: measured.length,
    avgEpsSurprise: mean(
      points.filter((p) => p.reported && p.epsSurprise != null).map((p) => p.epsSurprise as number)
    ),
    avgRevenueSurprise: mean(
      points
        .filter((p) => p.reported && p.revenueSurprise != null)
        .map((p) => p.revenueSurprise as number)
    ),
    next,
  };
}

/**
 * Splits prepared rows into the charted window and the next scheduled report.
 *
 * `quarters` caps the settled history; null means keep all of it. Only ONE
 * upcoming quarter is kept — FMP sometimes lists several placeholder dates a
 * year out, and a row of empty bars says nothing.
 */
export function windowEarnings(
  points: EarningsPoint[],
  quarters: number | null
): { history: EarningsPoint[]; next: EarningsPoint | null } {
  const reported = points.filter((p) => p.reported);
  const upcoming = points.filter((p) => !p.reported);

  return {
    history: quarters == null ? reported : reported.slice(-quarters),
    next: upcoming.length ? upcoming[0] : null,
  };
}
